// Form rules for the network APIs, POST /networks and PATCH /networks/{id}.
// Checked against the live service on 2026-09-16: marketId and chainId are
// sent as strings, minConfirmations is a whole number of at least 1, and the
// amounts are non-negative decimal strings with up to 18 fractional digits.
//
// The service does NOT check that Min Withdraw is below Max Withdraw, so the
// dashboard enforces that itself.
import { asTrimmedString, compareDecimals, decimalProblem, integerProblem } from "config/decimals";

export const MIN_CONFIRMATIONS = 1;
export const MAX_NETWORK_DECIMALS = 18;

export const NETWORK_FIELDS = {
  minConfirmations: { label: "Min Confirmations", placeholder: "e.g. 12", hint: "Whole number, 1 or more." },
  minDeposit: { label: "Min Deposit", placeholder: "e.g. 10" },
  minWithdraw: { label: "Min Withdraw", placeholder: "e.g. 1" },
  maxWithdraw: { label: "Max Withdraw", placeholder: "e.g. 1000", hint: "Must be at least the Min Withdraw." },
  decimals: { label: "Total Decimals", placeholder: "e.g. 18", hint: "Whole number from 0 to 18." },
  contractAddress: { label: "Contract Address", placeholder: "0x..." },
};

// Error keys match the input ids the network forms already use.
export function validateNetworkForm({
  marketId,
  chainId,
  minConfirmations,
  minDeposit,
  minWithdraw,
  maxWithdraw,
  decimals,
  contractAddress,
  isToken = false,
  requireMarketAndChain = true,
  requireDecimals = false,
}) {
  const errors = {};

  if (requireMarketAndChain) {
    if (!marketId) errors.marketid = "Market ID is required";
    if (!chainId) errors.chainid = "Chain ID is required";
  }

  const confirmations = asTrimmedString(minConfirmations);
  if (!confirmations) {
    errors.minConfirmations = `${NETWORK_FIELDS.minConfirmations.label} are required`;
  } else {
    const problem = integerProblem(confirmations, { label: NETWORK_FIELDS.minConfirmations.label, min: MIN_CONFIRMATIONS });
    if (problem) errors.minConfirmations = problem;
  }

  // The service accepts zero, so these only have to be valid, non-negative numbers.
  for (const key of ["minDeposit", "minWithdraw", "maxWithdraw"]) {
    const value = asTrimmedString({ minDeposit, minWithdraw, maxWithdraw }[key]);
    if (!value) {
      errors[key] = `${NETWORK_FIELDS[key].label} is required`;
      continue;
    }
    const problem = decimalProblem(value, NETWORK_FIELDS[key].label, { allowZero: true });
    if (problem) errors[key] = problem;
  }

  // The rule the user asked for: the pair must not be the wrong way round.
  if (!errors.minWithdraw && !errors.maxWithdraw && compareDecimals(minWithdraw, maxWithdraw) > 0) {
    errors.maxWithdraw = `${NETWORK_FIELDS.maxWithdraw.label} must be at least the ${NETWORK_FIELDS.minWithdraw.label}`;
  }

  const decimalsText = asTrimmedString(decimals);
  if (!decimalsText) {
    if (requireDecimals) errors.decimals = `${NETWORK_FIELDS.decimals.label} is required`;
  } else {
    const problem = integerProblem(decimalsText, { label: NETWORK_FIELDS.decimals.label, min: 0, max: MAX_NETWORK_DECIMALS });
    if (problem) errors.decimals = problem;
  }

  if (isToken && !asTrimmedString(contractAddress)) {
    errors.contractAddress = `${NETWORK_FIELDS.contractAddress.label} is required`;
  }

  return errors;
}

// Call only after validateNetworkForm returns no errors. Pass includeMarketAndChain
// false when editing, since those cannot be changed.
export function buildNetworkPayload({
  marketId,
  chainId,
  minConfirmations,
  minDeposit,
  minWithdraw,
  maxWithdraw,
  decimals,
  contractAddress,
  isToken = false,
  includeMarketAndChain = true,
}) {
  const payload = {
    minConfirmations: parseInt(minConfirmations, 10),
    minDeposit: asTrimmedString(minDeposit),
    minWithdraw: asTrimmedString(minWithdraw),
    maxWithdraw: asTrimmedString(maxWithdraw),
  };

  const decimalsText = asTrimmedString(decimals);
  if (decimalsText) payload.decimals = parseInt(decimalsText, 10);

  if (includeMarketAndChain) {
    // The service rejects these as numbers; they must be strings.
    payload.marketId = String(marketId);
    payload.chainId = String(chainId);
    if (isToken) {
      payload.isToken = true;
      payload.contractAddress = asTrimmedString(contractAddress);
    }
  }

  return payload;
}
